import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import type { NpsRole, NpsUser } from '@/shared/types/nps';
import { NPS_ROLE_LABEL, NPS_ROLES, primaryNpsRole } from '../../constants/roles';
import { npsUserCreateSchema, npsUserUpdateSchema, type NpsUserCreateValues, type NpsUserUpdateValues } from '../../schemas/user.schema';
import { formatCpfCnpj, stripCpfCnpj } from '../../utils/format-cpf-cnpj';
import { generateSecurePassword } from '../../utils/generate-password';
import { createNpsUser, updateNpsUser } from '../../api/use-nps';

interface Props {
  user: NpsUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function NpsUserFormDialog({ user, open, onOpenChange, onSaved }: Props) {
  const editing = Boolean(user);
  const [showPassword, setShowPassword] = useState(false);

  const createForm = useForm<NpsUserCreateValues>({
    resolver: zodResolver(npsUserCreateSchema),
    defaultValues: { name: '', last_name: '', email: '', tax_identifier: '', password: '', role: 'org', legacy_user_id: '' },
  });

  const updateForm = useForm<NpsUserUpdateValues>({
    resolver: zodResolver(npsUserUpdateSchema),
    defaultValues: { name: '', last_name: '', tax_identifier: '', role: undefined, legacy_user_id: '' },
  });

  useEffect(() => {
    if (!open) return;
    if (user) {
      updateForm.reset({
        name: user.name,
        last_name: user.last_name ?? '',
        tax_identifier: user.tax_identifier ? formatCpfCnpj(user.tax_identifier) : '',
        role: primaryNpsRole(user.roles),
        legacy_user_id: user.legacy_user_id ?? '',
      });
    } else {
      createForm.reset({ name: '', last_name: '', email: '', tax_identifier: '', password: '', role: 'org', legacy_user_id: '' });
    }
  }, [open, user, createForm, updateForm]);

  const saveCreate = createForm.handleSubmit(async (values) => {
    try {
      await createNpsUser({
        ...values,
        tax_identifier: stripCpfCnpj(values.tax_identifier),
      });
      toast.success('Usuário criado.');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao criar usuário.');
    }
  });

  const saveUpdate = updateForm.handleSubmit(async (values) => {
    if (!user) return;
    try {
      await updateNpsUser(user.id, {
        ...values,
        tax_identifier: values.tax_identifier ? stripCpfCnpj(values.tax_identifier) : undefined,
      });
      toast.success('Usuário atualizado.');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao atualizar usuário.');
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? 'Editar usuário' : 'Novo usuário'}</DialogTitle>
        </DialogHeader>

        {editing ? (
          <form onSubmit={saveUpdate} className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome" error={updateForm.formState.errors.name?.message}>
              <Input {...updateForm.register('name')} />
            </Field>
            <Field label="Sobrenome" error={updateForm.formState.errors.last_name?.message}>
              <Input {...updateForm.register('last_name')} />
            </Field>
            <Field label="CPF/CNPJ" error={updateForm.formState.errors.tax_identifier?.message}>
              <Input
                value={updateForm.watch('tax_identifier') ?? ''}
                onChange={(e) => updateForm.setValue('tax_identifier', formatCpfCnpj(e.target.value))}
              />
            </Field>
            <Field label="ID legado (Passbook)">
              <Input {...updateForm.register('legacy_user_id')} />
            </Field>
            <Field label="Perfil" className="sm:col-span-2">
              <Select
                key={updateForm.watch('role') ?? 'unset'}
                value={updateForm.watch('role')}
                onValueChange={(v) => updateForm.setValue('role', v as NpsRole)}
              >
                <SelectTrigger><SelectValue placeholder="Selecione o perfil" /></SelectTrigger>
                <SelectContent>
                  {NPS_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{NPS_ROLE_LABEL[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" loading={updateForm.formState.isSubmitting}>Salvar</Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={saveCreate} className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome" error={createForm.formState.errors.name?.message}>
              <Input {...createForm.register('name')} />
            </Field>
            <Field label="Sobrenome" error={createForm.formState.errors.last_name?.message}>
              <Input {...createForm.register('last_name')} />
            </Field>
            <Field label="E-mail" error={createForm.formState.errors.email?.message} className="sm:col-span-2">
              <Input type="email" {...createForm.register('email')} />
            </Field>
            <Field label="CPF/CNPJ" error={createForm.formState.errors.tax_identifier?.message}>
              <Input
                value={createForm.watch('tax_identifier')}
                onChange={(e) => createForm.setValue('tax_identifier', formatCpfCnpj(e.target.value))}
              />
            </Field>
            <Field label="ID legado (Passbook)">
              <Input {...createForm.register('legacy_user_id')} />
            </Field>
            <Field label="Senha" error={createForm.formState.errors.password?.message} className="sm:col-span-2">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input type={showPassword ? 'text' : 'password'} {...createForm.register('password')} />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPassword((v) => !v)}>
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
                <Button type="button" variant="secondary" onClick={() => createForm.setValue('password', generateSecurePassword())}>
                  <RefreshCw className="size-4" /> Gerar
                </Button>
              </div>
            </Field>
            <Field label="Perfil" className="sm:col-span-2">
              <Select
                value={createForm.watch('role')}
                onValueChange={(v) => createForm.setValue('role', v as NpsRole)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NPS_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{NPS_ROLE_LABEL[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" loading={createForm.formState.isSubmitting}>Criar usuário</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-2 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
